import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Shield, Eye, EyeOff, Lock, CheckCircle, Globe, ArrowRight, KeyRound, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import versionInfo from '../version.json';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CheckPattern = () => (
  <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="check-lines" x="0" y="0" width="100" height="40" patternUnits="userSpaceOnUse">
        <line x1="0" y1="39" x2="100" y2="39" stroke="#cbd5e1" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#check-lines)" opacity="0.3" />
  </svg>
);

export const LoginPage = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [clientIP, setClientIP] = useState('');

  // 2FA state
  const [twoFAMode, setTwoFAMode] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    const fetchClientInfo = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/client-info`);
        setClientIP(response.data.ip_address);
      } catch (error) {}
    };
    fetchClientInfo();
  }, []);

  const [footerText, setFooterText] = useState('');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/app-settings/public`)
      .then(r => r.json())
      .then(d => {
        const parts = [];
        if (d.copyright_text) parts.push(d.copyright_text);
        parts.push(`Version ${versionInfo.version}`);
        setFooterText(parts.join(' \u00b7 '));
      })
      .catch(() => {
        setFooterText(`Version ${versionInfo.version}`);
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password });
      if (res.data.requires_2fa) {
        setSessionId(res.data.session_id);
        setMaskedEmail(res.data.masked_email);
        setTwoFAMode(true);
        setCode(['', '', '', '', '', '']);
        toast.success('Verification code sent to your email');
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else {
        login(null, null, res.data);
        toast.success('Welcome back!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const verifyingRef = useRef(false);

  const handleCodeChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newCode.every(c => c) && !verifyingRef.current) {
      handleVerify2FA(newCode.join(''));
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
      handleVerify2FA(pasted);
    }
  };

  const handleVerify2FA = async (codeStr) => {
    if (verifyingRef.current) return;
    const fullCode = codeStr || code.join('');
    if (fullCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    verifyingRef.current = true;
    setVerifying(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/verify-2fa`, { session_id: sessionId, code: fullCode });
      login(null, null, res.data);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
      setCode(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      verifyingRef.current = false;
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-2fa`, { session_id: sessionId });
      toast.success('New code sent');
      setCode(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  const handleBack = () => {
    setTwoFAMode(false);
    setSessionId('');
    setCode(['', '', '', '', '', '']);
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" style={{ backgroundColor: '#e8f4f0' }}>
      <div className="flex-1 flex items-center justify-center p-4">
      <CheckPattern />
      
      <div className="absolute top-16 left-8 opacity-[0.25] rotate-[-12deg] pointer-events-none">
        <div className="w-[420px] h-48 border-2 border-slate-400 rounded-sm bg-white shadow-md">
          <div className="p-4 h-full flex flex-col">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="h-3 w-36 bg-slate-300 rounded mb-1"></div>
                <div className="h-2 w-28 bg-slate-200 rounded"></div>
              </div>
              <div className="text-right">
                <div className="h-2 w-16 bg-slate-200 rounded mb-1"></div>
                <div className="h-4 w-20 border border-slate-300 rounded text-xs flex items-center justify-center text-slate-400">1001</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[10px] text-slate-400 mb-1">PAY TO THE ORDER OF</div>
              <div className="border-b border-slate-300 pb-1 flex justify-between items-end">
                <div className="h-3 w-48 bg-slate-200 rounded"></div>
                <div className="h-5 w-24 border border-slate-300 rounded text-xs flex items-center justify-center text-slate-400">$0,000.00</div>
              </div>
            </div>
            <div className="mt-3 border-b border-slate-300 pb-1">
              <div className="h-2 w-56 bg-slate-200 rounded"></div>
            </div>
            <div className="mt-auto flex justify-between items-end">
              <div className="h-2 w-32 bg-slate-200 rounded"></div>
              <div className="border-t border-slate-300 w-40 pt-1">
                <div className="h-2 w-full bg-slate-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="absolute bottom-16 right-8 opacity-[0.22] rotate-[8deg] pointer-events-none">
        <div className="w-[400px] h-44 border-2 border-slate-400 rounded-sm bg-white shadow-md">
          <div className="p-4 h-full flex flex-col">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="h-3 w-32 bg-slate-300 rounded mb-1"></div>
                <div className="h-2 w-24 bg-slate-200 rounded"></div>
              </div>
              <div className="text-right">
                <div className="h-2 w-14 bg-slate-200 rounded mb-1"></div>
                <div className="h-4 w-16 border border-slate-300 rounded"></div>
              </div>
            </div>
            <div className="mt-3">
              <div className="text-[10px] text-slate-400 mb-1">PAY TO THE ORDER OF</div>
              <div className="border-b border-slate-300 pb-1 flex justify-between items-end">
                <div className="h-3 w-44 bg-slate-200 rounded"></div>
                <div className="h-5 w-20 border border-slate-300 rounded"></div>
              </div>
            </div>
            <div className="mt-2 border-b border-slate-300 pb-1">
              <div className="h-2 w-48 bg-slate-200 rounded"></div>
            </div>
            <div className="mt-auto flex justify-between items-end">
              <div className="h-2 w-28 bg-slate-200 rounded"></div>
              <div className="border-t border-slate-300 w-36 pt-1">
                <div className="h-2 w-full bg-slate-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="absolute top-32 right-16 opacity-[0.18] rotate-[4deg] pointer-events-none hidden xl:block">
        <div className="w-[340px] h-36 border-2 border-slate-400 rounded-sm bg-white shadow-md">
          <div className="p-3 h-full flex flex-col">
            <div className="flex justify-between">
              <div className="h-2 w-28 bg-slate-300 rounded"></div>
              <div className="h-3 w-14 border border-slate-300 rounded"></div>
            </div>
            <div className="mt-3 border-b border-slate-300 pb-1">
              <div className="h-2 w-36 bg-slate-200 rounded"></div>
            </div>
            <div className="mt-2 border-b border-slate-300 pb-1">
              <div className="h-2 w-40 bg-slate-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-md fade-in relative z-10">
        <div className="text-center -mb-6">
          <img src="/logo.png" alt="PayMasterGeneral" className="w-full max-w-md object-contain" />
        </div>

        <Card className="border-2 border-slate-300 shadow-xl bg-white/95 backdrop-blur-sm">
          {!twoFAMode ? (
            <>
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-xl font-semibold">Sign In</CardTitle>
                <CardDescription>
                  Enter your credentials to access the dashboard
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="superadmin@admin.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-700">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="login-password-input"
                        className="h-12 pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        data-testid="toggle-password-btn"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="remember-me"
                        checked={rememberMe}
                        onCheckedChange={setRememberMe}
                        data-testid="remember-me-switch"
                      />
                      <Label htmlFor="remember-me" className="text-sm text-slate-600 cursor-pointer">
                        Remember me
                      </Label>
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-slate-100 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span>256-bit SSL</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                      <Lock className="w-4 h-4 text-blue-600" />
                      <span>Encrypted</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>Verified</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                      <Shield className="w-4 h-4 text-amber-600" />
                      <span>Protected</span>
                    </div>
                  </div>
                  
                  {clientIP && (
                    <div className="flex items-center justify-center gap-2 text-xs text-slate-400 bg-slate-50 rounded-lg px-3 py-2">
                      <Globe className="w-4 h-4" />
                      <span>Your IP: </span>
                      <code className="font-mono text-slate-600">{clientIP}</code>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Secure authentication enabled</span>
                  </div>

                  <Link
                    to="/learn-more"
                    className="flex items-center justify-center gap-2 text-sm bg-slate-900 text-white hover:bg-slate-800 font-medium transition-colors rounded-lg py-2.5 px-4"
                    data-testid="learn-more-link"
                  >
                    Learn more about PayMasterGeneral <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="space-y-1 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                    <KeyRound className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-semibold">Verify Your Identity</CardTitle>
                    <CardDescription>
                      Enter the 6-digit code sent to {maskedEmail}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
                    {code.map((digit, i) => (
                      <input
                        key={i}
                        ref={el => inputRefs.current[i] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(i, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(i, e)}
                        className="w-12 h-14 text-center text-2xl font-mono font-bold border-2 border-slate-300 rounded-lg focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-colors"
                        data-testid={`2fa-code-input-${i}`}
                      />
                    ))}
                  </div>

                  <Button
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                    disabled={verifying || code.some(c => !c)}
                    onClick={() => handleVerify2FA()}
                    data-testid="verify-2fa-btn"
                  >
                    {verifying ? 'Verifying...' : 'Verify & Sign In'}
                  </Button>

                  <div className="flex items-center justify-between pt-2">
                    <button
                      onClick={handleBack}
                      className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                      data-testid="back-to-login-btn"
                    >
                      <ArrowLeft className="w-4 h-4" /> Back to login
                    </button>
                    <button
                      onClick={handleResend}
                      disabled={resending}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors disabled:opacity-50"
                      data-testid="resend-code-btn"
                    >
                      {resending ? 'Sending...' : 'Resend Code'}
                    </button>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500 text-center">
                    Code expires in 5 minutes. Check your spam folder if you don't see it.
                  </div>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
      </div>
      {footerText && (
        <div style={{ textAlign: 'center', padding: '16px 0', color: '#94a3b8', fontSize: '12px' }}>
          {footerText}
        </div>
      )}
    </div>
  );
};
